// screens/SiteImagesAdminScreen.jsx
//
// HADACY · admin · imágenes del sitio
//
// On-brand redesign of the site-images admin. The previous version asked
// admins to remember what each cryptic key meant (`principal`, `seccion2`,
// `vision3`, `banner_descuentos_mobile`...) and only revealed the
// recommended dimensions *after* selecting a key in a dropdown.
//
// This rewrite swaps that for a visual slot directory: every slot the
// storefront knows about is rendered as its own card, grouped by where it
// appears on the page (Hero · Promociones · Secciones · ¿Por qué HADACY?),
// with a thumbnail of the current image (or an empty placeholder), a
// plain-Spanish label, where on the site it shows up, and the recommended
// dimensions. Clicking a card opens the upload panel preset to that slot,
// so admins never have to think about keys again — they see what they're
// editing.
//
// All existing upload/validation logic (Cloudinary upload, browser
// compression, banner / mobile-hero / vision dimension rules, 10MB cap)
// is preserved unchanged from the previous version.

import React, { useEffect, useState, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Loader from "../components/Loader";
import {
  fetchSiteImages,
  upsertSiteImage,
  deleteSiteImage,
} from "../actions/siteImageActions";

const CLOUDINARY_UPLOAD_URL =
  "https://api.cloudinary.com/v1_1/cc1991/image/upload";
const CLOUDINARY_PRESET = "HADACY";

// ─── Slot catalog ────────────────────────────────────────────────────────
// Every image the storefront looks up by key is registered here. Adding a
// new slot means appending a row to this list — the grid, validation
// dispatch, status pills and "todas las claves usadas" banner all derive
// from this single source of truth.
const SLOT_GROUPS = [
  { id: "hero", label: "Banner principal (Hero)" },
  { id: "promociones", label: "Banners de promociones" },
  { id: "secciones", label: "Secciones de la home" },
  { id: "vision", label: "¿Por qué HADACY?" },
];

const SLOTS = [
  {
    key: "principal",
    group: "hero",
    label: "Hero · Escritorio",
    where: "Banner principal de la home en computadora y tablet.",
    recommended: "1920×800 px, apaisado.",
    aspect: "16/7",
  },
  {
    key: "principal_mobile",
    group: "hero",
    label: "Hero · Móvil",
    where: "Banner principal de la home en teléfonos (vertical).",
    recommended: "1080×1920 px (vertical, mínimo 720×1280).",
    aspect: "9/16",
  },
  {
    key: "banner_descuentos",
    group: "promociones",
    label: "Promo · Escritorio",
    where: "Cinta promocional bajo el hero en escritorio.",
    recommended: "1600×400 px (mínimo 1200×300, proporción ≥ 3.5).",
    aspect: "4/1",
  },
  {
    key: "banner_descuentos_mobile",
    group: "promociones",
    label: "Promo · Móvil",
    where:
      "Cinta promocional en teléfonos. Si no se sube, se usa la versión de escritorio.",
    recommended: "1080×1080 (cuadrado) o 1080×1350 (vertical IG).",
    aspect: "1/1",
  },
  {
    key: "seccion1",
    group: "secciones",
    label: "Sección 1",
    where: "Tarjeta de sección 1 en la home.",
    recommended: "1200×800 px, apaisada o cuadrada.",
    aspect: "3/2",
  },
  {
    key: "seccion2",
    group: "secciones",
    label: "Sección 2",
    where: "Tarjeta de sección 2 en la home.",
    recommended: "1200×800 px, apaisada o cuadrada.",
    aspect: "3/2",
  },
  {
    key: "seccion3",
    group: "secciones",
    label: "Sección 3",
    where: "Tarjeta de sección 3 en la home.",
    recommended: "1200×800 px, apaisada o cuadrada.",
    aspect: "3/2",
  },
  {
    key: "vision1",
    group: "vision",
    label: "Por qué HADACY · 1",
    where: "Tarjeta 1 de la sección '¿Por qué HADACY?' en la home.",
    recommended:
      "1000×1000 px (cuadrada), fondo transparente o crema, motivo centrado.",
    aspect: "1/1",
  },
  {
    key: "vision2",
    group: "vision",
    label: "Por qué HADACY · 2",
    where: "Tarjeta 2 de la sección '¿Por qué HADACY?' en la home.",
    recommended:
      "1000×1000 px (cuadrada), fondo transparente o crema, motivo centrado.",
    aspect: "1/1",
  },
  {
    key: "vision3",
    group: "vision",
    label: "Por qué HADACY · 3",
    where: "Tarjeta 3 de la sección '¿Por qué HADACY?' en la home.",
    recommended:
      "1000×1000 px (cuadrada), fondo transparente o crema, motivo centrado.",
    aspect: "1/1",
  },
];

const KNOWN_KEYS = SLOTS.map((s) => s.key);
const SLOT_BY_KEY = SLOTS.reduce((acc, s) => {
  acc[s.key] = s;
  return acc;
}, {});

// ─── Image helpers (kept identical to the previous version) ─────────────
async function compressImage(
  file,
  { maxWidth = 1800, maxHeight = 1800, quality = 0.75, format = "image/webp" } = {}
) {
  const img = new Image();
  img.crossOrigin = "anonymous";
  const src = URL.createObjectURL(file);
  await new Promise((res, rej) => {
    img.onload = res;
    img.onerror = rej;
    img.src = src;
  });

  const { width, height } = img;
  const ratio = Math.min(maxWidth / width, maxHeight / height, 1);
  const tw = Math.round(width * ratio);
  const th = Math.round(height * ratio);

  const canvas = document.createElement("canvas");
  canvas.width = tw;
  canvas.height = th;
  const ctx = canvas.getContext("2d");
  ctx.drawImage(img, 0, 0, tw, th);

  const blob = await new Promise((res) => canvas.toBlob(res, format, quality));
  URL.revokeObjectURL(src);
  return blob;
}

function fmtMB(bytes) {
  return (bytes / (1024 * 1024)).toFixed(1);
}

function readImageMeta(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      const meta = {
        width: img.naturalWidth,
        height: img.naturalHeight,
        ratio: img.naturalWidth / img.naturalHeight,
      };
      URL.revokeObjectURL(url);
      resolve(meta);
    };
    img.onerror = reject;
    img.src = url;
  });
}

// ─── Per-slot validation rules ──────────────────────────────────────────
const BANNER_RULES = { minWidth: 1200, minRatio: 3.5 };
const MOBILE_BANNER_RULES = {
  minWidth: 800,
  minHeight: 800,
  minRatio: 0.6,
  maxRatio: 1.2,
};
const MOBILE_HERO_RULES = {
  minWidth: 720,
  minHeight: 1280,
  minPortraitRatio: 1.6,
};
const VISION_RULES = {
  minWidth: 600,
  minHeight: 600,
  minRatio: 0.7,
  maxRatio: 1.6,
};

const fmtDate = (v) =>
  v
    ? new Date(v).toLocaleString("es-MX", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "—";

const SiteImagesAdminScreen = () => {
  const [deletingIds, setDeletingIds] = useState(new Set());
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { userInfo } = useSelector((s) => s.userLogin);
  const { loading, error, images } = useSelector((s) => s.siteImages);

  const [keyName, setKeyName] = useState("principal");
  const [alt, setAlt] = useState("");
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [filter, setFilter] = useState("");
  const fileInputRef = useRef(null);
  const formRef = useRef(null);

  const [toast, setToast] = useState({ show: false, message: "", tone: "ok" });
  const showToast = (message, tone = "ok") =>
    setToast({ show: true, message, tone });

  // Auto-dismiss toasts so the corner doesn't stack indefinitely.
  useEffect(() => {
    if (!toast.show) return undefined;
    const t = setTimeout(
      () => setToast((prev) => ({ ...prev, show: false })),
      3500
    );
    return () => clearTimeout(t);
  }, [toast.show]);

  useEffect(() => {
    if (!userInfo || !userInfo.isAdmin) {
      navigate("/login");
      return;
    }
    dispatch(fetchSiteImages());
  }, [dispatch, navigate, userInfo]);

  // Map of key → image record so each slot card can look itself up in O(1)
  // instead of scanning the full list on every render.
  const imagesByKey = useMemo(() => {
    const m = {};
    (images || []).forEach((img) => {
      if (img && img.key) m[img.key] = img;
    });
    return m;
  }, [images]);

  const usedKeys = useMemo(
    () => new Set(Object.keys(imagesByKey)),
    [imagesByKey]
  );
  const allKeysUsed = usedKeys.size >= KNOWN_KEYS.length;

  // Filtered slot list — applies to the directory below.
  const filteredSlots = useMemo(() => {
    const f = (filter || "").toLowerCase().trim();
    if (!f) return SLOTS;
    return SLOTS.filter((s) => {
      const img = imagesByKey[s.key];
      return (
        s.key.toLowerCase().includes(f) ||
        s.label.toLowerCase().includes(f) ||
        s.where.toLowerCase().includes(f) ||
        (img?.alt || "").toLowerCase().includes(f) ||
        (img?.publicId || "").toLowerCase().includes(f)
      );
    });
  }, [filter, imagesByKey]);

  // Pre-bin the filtered slots by group so the JSX can just iterate the
  // groups + slots already partitioned.
  const slotsByGroup = useMemo(() => {
    const buckets = {};
    SLOT_GROUPS.forEach((g) => (buckets[g.id] = []));
    filteredSlots.forEach((s) => {
      if (!buckets[s.group]) buckets[s.group] = [];
      buckets[s.group].push(s);
    });
    return buckets;
  }, [filteredSlots]);

  // If the active form key gets occupied (e.g., we just saved that slot)
  // bounce to the first free slot so the form stays useful.
  useEffect(() => {
    if (!KNOWN_KEYS.includes(keyName)) return;
    if (usedKeys.has(keyName)) {
      const firstFree = KNOWN_KEYS.find((k) => !usedKeys.has(k));
      if (firstFree) setKeyName(firstFree);
    }
  }, [usedKeys, keyName]);

  const MAX_MB = 10;
  const MAX_BYTES = MAX_MB * 1024 * 1024;

  const uploadToCloudinary = async (fileOrBlob) => {
    const fd = new FormData();
    fd.append("file", fileOrBlob);
    fd.append("upload_preset", CLOUDINARY_PRESET);

    const res = await fetch(CLOUDINARY_UPLOAD_URL, {
      method: "POST",
      body: fd,
    });

    if (!res.ok) {
      let details = "";
      try {
        const txt = await res.text();
        details = txt;
        const maybe = JSON.parse(txt);
        if (maybe?.error?.message) details = maybe.error.message;
      } catch {}
      throw new Error(`Cloudinary: ${details || `HTTP ${res.status}`}`);
    }

    return await res.json();
  };

  const resetFormAfterSave = () => {
    setFile(null);
    setAlt("");
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  // Pick a slot from the directory → preselects the form, scrolls it into
  // view, and prefills alt with whatever was last there.
  const onPickSlot = (slot) => {
    if (usedKeys.has(slot.key)) {
      showToast(
        `La clave "${slot.key}" ya está en uso. Elimínala para reemplazarla.`,
        "warn"
      );
      return;
    }
    setKeyName(slot.key);
    setAlt("");
    setFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (formRef.current && typeof window !== "undefined") {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // ─── Submit ──────────────────────────────────────────────────────────
  const onCreateOrReplace = async (e) => {
    e.preventDefault();

    if (usedKeys.has(keyName)) {
      showToast(`La clave "${keyName}" ya está en uso.`, "warn");
      return;
    }
    if (!keyName.trim()) {
      showToast("Selecciona una clave.", "warn");
      return;
    }
    if (!file) {
      showToast("Elige un archivo de imagen.", "warn");
      return;
    }

    if (keyName.trim() === "banner_descuentos") {
      try {
        const meta = await readImageMeta(file);
        if (
          meta.width < BANNER_RULES.minWidth ||
          meta.ratio < BANNER_RULES.minRatio
        ) {
          showToast(
            `El banner debe ser ancho (≥${BANNER_RULES.minWidth}px y razón ≥${BANNER_RULES.minRatio}). Imagen actual: ${meta.width}×${meta.height} (ratio ${meta.ratio.toFixed(2)}).`,
            "warn"
          );
          return;
        }
      } catch {
        showToast("No se pudo leer la imagen para validarla.", "danger");
        return;
      }
    }
    if (keyName.trim() === "principal_mobile") {
      try {
        const meta = await readImageMeta(file);
        const portraitRatio = meta.height / meta.width;
        if (
          meta.width < MOBILE_HERO_RULES.minWidth ||
          meta.height < MOBILE_HERO_RULES.minHeight ||
          portraitRatio < MOBILE_HERO_RULES.minPortraitRatio
        ) {
          showToast(
            `El banner principal móvil debe ser VERTICAL y grande. Recomendado 1080×1920 (mínimo ${MOBILE_HERO_RULES.minWidth}×${MOBILE_HERO_RULES.minHeight}), y relación (alto/ancho) ≥ ${MOBILE_HERO_RULES.minPortraitRatio}. Actual: ${meta.width}×${meta.height} (ratio ${portraitRatio.toFixed(2)}).`,
            "warn"
          );
          return;
        }
      } catch {
        showToast("No se pudo leer la imagen para validarla.", "danger");
        return;
      }
    }
    if (keyName.trim() === "banner_descuentos_mobile") {
      try {
        const meta = await readImageMeta(file);
        if (
          meta.width < MOBILE_BANNER_RULES.minWidth ||
          meta.height < MOBILE_BANNER_RULES.minHeight ||
          meta.ratio < MOBILE_BANNER_RULES.minRatio ||
          meta.ratio > MOBILE_BANNER_RULES.maxRatio
        ) {
          showToast(
            `El banner móvil debe ser cuadrado o ligeramente vertical, mínimo ${MOBILE_BANNER_RULES.minWidth}×${MOBILE_BANNER_RULES.minHeight}px, con relación ancho/alto entre ${MOBILE_BANNER_RULES.minRatio} y ${MOBILE_BANNER_RULES.maxRatio}. Actual: ${meta.width}×${meta.height} (ratio ${meta.ratio.toFixed(2)}).`,
            "warn"
          );
          return;
        }
      } catch {
        showToast("No se pudo leer la imagen para validarla.", "danger");
        return;
      }
    }
    if (/^vision[123]$/.test(keyName.trim())) {
      try {
        const meta = await readImageMeta(file);
        if (
          meta.width < VISION_RULES.minWidth ||
          meta.height < VISION_RULES.minHeight ||
          meta.ratio < VISION_RULES.minRatio ||
          meta.ratio > VISION_RULES.maxRatio
        ) {
          showToast(
            `Las imágenes de la sección "¿Por qué HADACY?" deben ser cuadradas o ligeramente apaisadas, mínimo ${VISION_RULES.minWidth}×${VISION_RULES.minHeight}px, con relación entre ${VISION_RULES.minRatio} y ${VISION_RULES.maxRatio}. Actual: ${meta.width}×${meta.height} (ratio ${meta.ratio.toFixed(2)}).`,
            "warn"
          );
          return;
        }
      } catch {
        showToast("No se pudo leer la imagen para validarla.", "danger");
        return;
      }
    }

    try {
      setUploading(true);

      let quality = 0.8;
      let blob = await compressImage(file, {
        maxWidth: 1800,
        maxHeight: 1800,
        quality,
        format: "image/webp",
      });

      while (blob.size > MAX_BYTES && quality > 0.4) {
        quality -= 0.1;
        blob = await compressImage(file, {
          maxWidth: 1800,
          maxHeight: 1800,
          quality,
          format: "image/webp",
        });
      }

      if (blob.size > MAX_BYTES) {
        setUploading(false);
        return showToast(
          `Incluso comprimida pesa ${fmtMB(blob.size)} MB (máximo ${MAX_MB} MB).`,
          "warn"
        );
      }

      const result = await uploadToCloudinary(blob);

      await dispatch(
        upsertSiteImage({
          key: keyName.trim(),
          url: result.secure_url,
          secureUrl: result.secure_url,
          publicId: result.public_id,
          alt,
          tags: [],
        })
      );

      await dispatch(fetchSiteImages());
      setUploading(false);
      resetFormAfterSave();
      showToast("Imagen guardada correctamente.", "ok");
    } catch (err) {
      console.error(err);
      setUploading(false);
      showToast(err?.message || "No se pudo guardar la imagen.", "danger");
    }
  };

  const onDelete = async (img) => {
    const slot = SLOT_BY_KEY[img.key];
    const niceLabel = slot ? slot.label : img.key;
    if (
      !window.confirm(
        `¿Eliminar la imagen "${niceLabel}"? La sección quedará vacía hasta que subas una nueva.`
      )
    )
      return;

    setDeletingIds((prev) => new Set(prev).add(img._id));
    try {
      await dispatch(deleteSiteImage(img._id));
      await dispatch(fetchSiteImages());
      showToast("Imagen eliminada.", "ok");
    } catch {
      showToast("No se pudo eliminar la imagen.", "danger");
    } finally {
      setDeletingIds((prev) => {
        const next = new Set(prev);
        next.delete(img._id);
        return next;
      });
    }
  };

  const activeSlot = SLOT_BY_KEY[keyName] || SLOTS[0];

  return (
    <div className="hadacy-images">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600&family=Jost:wght@200;300;400;500&display=swap');

        /* ─── HADACY · admin · imágenes del sitio ─────────────────────
           Same cream/gold token system as the rest of the admin so
           visiting the screen feels continuous with orders, users, and
           coupons. */
        .hadacy-images {
          --cream: #f5f0e8;
          --dark: #1a1410;
          --gold: #b8933a;
          --gold-light: #d4af6a;
          --muted: #7a6e62;
          --card-bg: #faf7f2;
          --recess: #efe7d8;
          --hairline: rgba(184, 147, 58, 0.18);
          --danger: #b04a3c;
          --success: #5b7a3c;
          font-family: "Jost", sans-serif;
          color: var(--dark);
          max-width: 1320px;
          margin: 1.6rem auto 3rem;
          padding: 0 1.2rem;
        }

        /* Header — eyebrow + serif title + lede. */
        .hadacy-images__header { margin-bottom: 1.4rem; }
        .hadacy-images__eyebrow {
          font-size: 0.66rem;
          letter-spacing: 0.42em;
          text-transform: uppercase;
          color: var(--gold);
          margin-bottom: 0.4rem;
        }
        .hadacy-images__title {
          font-family: "Cormorant Garamond", serif;
          font-weight: 400;
          font-size: 2.1rem;
          line-height: 1.15;
          color: var(--dark);
          margin: 0 0 0.4rem 0;
        }
        .hadacy-images__lede {
          font-size: 0.92rem;
          color: var(--muted);
          line-height: 1.55;
          max-width: 680px;
          margin: 0;
        }

        /* Help band explaining the workflow once. */
        .hadacy-images-help {
          background: var(--card-bg);
          border: 1px solid var(--hairline);
          border-radius: 14px;
          padding: 1.1rem 1.25rem;
          margin: 1.2rem 0 1.6rem;
          display: grid;
          grid-template-columns: 1fr 1fr 1fr;
          gap: 1rem;
        }
        @media (max-width: 880px) {
          .hadacy-images-help { grid-template-columns: 1fr; }
        }
        .hadacy-images-help__item {
          display: flex; gap: 0.85rem;
        }
        .hadacy-images-help__num {
          flex: 0 0 auto;
          width: 32px; height: 32px;
          border-radius: 999px;
          background: rgba(184, 147, 58, 0.12);
          color: var(--gold);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-family: "Cormorant Garamond", serif;
          font-size: 1rem;
          font-weight: 500;
        }
        .hadacy-images-help__title {
          font-size: 0.84rem;
          color: var(--dark);
          font-weight: 500;
          margin: 0 0 0.2rem 0;
        }
        .hadacy-images-help__body {
          font-size: 0.78rem;
          color: var(--muted);
          line-height: 1.5;
          margin: 0;
        }

        /* Generic panel (form + group sections). */
        .hadacy-images-panel {
          background: var(--card-bg);
          border: 1px solid var(--hairline);
          border-radius: 16px;
          padding: 1.5rem 1.5rem;
          box-shadow: 0 14px 40px rgba(26, 20, 16, 0.04);
          margin-bottom: 1.5rem;
        }
        .hadacy-images-panel__head {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.8rem;
          padding-bottom: 0.9rem;
          margin-bottom: 1.1rem;
          border-bottom: 1px solid var(--hairline);
          flex-wrap: wrap;
        }
        .hadacy-images-panel__title {
          font-family: "Cormorant Garamond", serif;
          font-size: 1.5rem;
          color: var(--dark);
          margin: 0;
          font-weight: 400;
        }
        .hadacy-images-panel__hint {
          font-size: 0.78rem;
          color: var(--muted);
          letter-spacing: 0.04em;
        }

        /* Upload form layout — two-column on desktop, stacks on mobile. */
        .hadacy-images-upload {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          gap: 1.4rem;
        }
        @media (max-width: 880px) {
          .hadacy-images-upload { grid-template-columns: 1fr; }
        }

        .hadacy-images-section { margin-bottom: 1.2rem; }
        .hadacy-images-section:last-of-type { margin-bottom: 0.4rem; }
        .hadacy-images-section__title {
          font-size: 0.62rem;
          letter-spacing: 0.28em;
          text-transform: uppercase;
          color: var(--gold);
          margin: 0 0 0.85rem 0;
          font-weight: 500;
          display: flex;
          align-items: center;
          gap: 0.5rem;
        }
        .hadacy-images-section__title::after {
          content: "";
          flex: 1;
          height: 1px;
          background: var(--hairline);
        }

        .hadacy-images-field { margin-bottom: 0.95rem; }
        .hadacy-images-field:last-child { margin-bottom: 0; }
        .hadacy-images-label {
          display: block;
          font-size: 0.62rem;
          letter-spacing: 0.22em;
          text-transform: uppercase;
          color: var(--muted);
          font-weight: 400;
          margin-bottom: 0.35rem;
        }
        .hadacy-images-input,
        .hadacy-images-select {
          width: 100%;
          background: #fff;
          border: 1px solid rgba(184, 147, 58, 0.22);
          border-radius: 10px;
          padding: 0.65rem 0.9rem;
          font-family: "Jost", sans-serif;
          font-size: 0.93rem;
          color: var(--dark);
          transition: border-color .2s ease, box-shadow .2s ease;
        }
        .hadacy-images-input:focus,
        .hadacy-images-select:focus {
          outline: none;
          border-color: var(--gold);
          box-shadow: 0 0 0 3px rgba(184, 147, 58, 0.14);
        }
        .hadacy-images-input::placeholder { color: var(--muted); font-style: italic; }
        .hadacy-images-help-text {
          display: block;
          font-size: 0.78rem;
          color: var(--muted);
          line-height: 1.5;
          margin-top: 0.35rem;
        }
        .hadacy-images-help-text strong { color: var(--dark); font-weight: 500; }

        /* Slot detail card — shows what will be replaced. */
        .hadacy-images-slot-detail {
          background: var(--recess);
          border: 1px solid var(--hairline);
          border-radius: 12px;
          padding: 0.85rem 0.95rem;
        }
        .hadacy-images-slot-detail__label {
          font-size: 0.6rem;
          letter-spacing: 0.24em;
          text-transform: uppercase;
          color: var(--gold);
          font-weight: 500;
          margin-bottom: 0.3rem;
        }
        .hadacy-images-slot-detail__title {
          font-family: "Cormorant Garamond", serif;
          font-size: 1.15rem;
          color: var(--dark);
          margin: 0 0 0.3rem 0;
          line-height: 1.2;
        }
        .hadacy-images-slot-detail__body {
          font-size: 0.82rem;
          color: var(--muted);
          line-height: 1.5;
          margin: 0;
        }
        .hadacy-images-slot-detail__body strong {
          color: var(--dark);
          font-weight: 500;
        }

        /* Drop zone */
        .hadacy-images-drop {
          position: relative;
          border: 2px dashed var(--hairline);
          border-radius: 12px;
          padding: 1.5rem 1.2rem;
          text-align: center;
          background: #fff;
          cursor: pointer;
          transition: border-color .2s ease, background .2s ease;
        }
        .hadacy-images-drop:hover {
          border-color: var(--gold);
          background: rgba(184, 147, 58, 0.04);
        }
        .hadacy-images-drop input[type="file"] {
          position: absolute;
          inset: 0;
          width: 100%;
          height: 100%;
          opacity: 0;
          cursor: pointer;
        }
        .hadacy-images-drop input[type="file"]:disabled { cursor: not-allowed; }
        .hadacy-images-drop__icon {
          font-size: 1.4rem;
          color: var(--gold);
          margin-bottom: 0.4rem;
        }
        .hadacy-images-drop__title {
          font-size: 0.92rem;
          color: var(--dark);
          margin: 0 0 0.2rem 0;
        }
        .hadacy-images-drop__body {
          font-size: 0.78rem;
          color: var(--muted);
          margin: 0;
        }
        .hadacy-images-drop--has-file {
          border-style: solid;
          border-color: rgba(91, 122, 60, 0.35);
          background: rgba(91, 122, 60, 0.04);
        }
        .hadacy-images-drop--disabled { opacity: 0.55; cursor: not-allowed; }

        /* Live preview window. */
        .hadacy-images-preview {
          background: var(--recess);
          border: 1px solid var(--hairline);
          border-radius: 12px;
          padding: 0.9rem;
          min-height: 220px;
          display: flex;
          flex-direction: column;
          gap: 0.7rem;
        }
        .hadacy-images-preview__label {
          font-size: 0.6rem;
          letter-spacing: 0.24em;
          text-transform: uppercase;
          color: var(--gold);
          font-weight: 500;
        }
        .hadacy-images-preview__frame {
          flex: 1;
          background: #fff;
          border: 1px solid var(--hairline);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0.6rem;
          min-height: 200px;
        }
        .hadacy-images-preview__frame img {
          max-width: 100%;
          max-height: 280px;
          border-radius: 6px;
          object-fit: contain;
        }
        .hadacy-images-preview__empty {
          font-size: 0.85rem;
          color: var(--muted);
          font-style: italic;
        }

        /* CTA buttons — pill primary + ghost. */
        .hadacy-images-actions {
          display: flex;
          gap: 0.65rem;
          margin-top: 1rem;
          flex-wrap: wrap;
        }
        .hadacy-images-cta {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 0.5rem;
          padding: 0.75rem 1.5rem;
          border-radius: 999px;
          background: var(--dark);
          border: 1px solid var(--dark);
          color: var(--gold-light);
          font-size: 0.7rem;
          letter-spacing: 0.24em;
          text-transform: uppercase;
          cursor: pointer;
          font-family: "Jost", sans-serif;
          transition: background .2s ease, color .2s ease, border-color .2s ease;
        }
        .hadacy-images-cta:hover:not(:disabled) {
          background: var(--gold);
          border-color: var(--gold);
          color: var(--dark);
        }
        .hadacy-images-cta:disabled { opacity: 0.65; cursor: not-allowed; }
        .hadacy-images-cta--ghost {
          background: transparent;
          color: var(--muted);
          border: 1px solid var(--hairline);
        }
        .hadacy-images-cta--ghost:hover:not(:disabled) {
          background: var(--recess);
          color: var(--dark);
          border-color: var(--gold);
        }

        /* Search bar (above slot directory). */
        .hadacy-images-search {
          position: relative;
          margin-bottom: 1rem;
        }
        .hadacy-images-search__input { padding-left: 2.4rem; }
        .hadacy-images-search__icon {
          position: absolute;
          top: 50%; left: 0.9rem;
          transform: translateY(-50%);
          color: var(--muted);
          pointer-events: none;
          font-size: 0.85rem;
        }

        /* Group accordion-like sections in the directory. */
        .hadacy-images-group { margin-bottom: 1.6rem; }
        .hadacy-images-group__head {
          display: flex;
          align-items: baseline;
          justify-content: space-between;
          gap: 0.6rem;
          margin-bottom: 0.85rem;
        }
        .hadacy-images-group__title {
          font-family: "Cormorant Garamond", serif;
          font-weight: 400;
          font-size: 1.4rem;
          color: var(--dark);
          margin: 0;
        }
        .hadacy-images-group__count {
          font-size: 0.72rem;
          letter-spacing: 0.16em;
          color: var(--muted);
          text-transform: uppercase;
        }

        .hadacy-images-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
          gap: 1rem;
        }

        /* Individual slot card. */
        .hadacy-image-card {
          background: var(--card-bg);
          border: 1px solid var(--hairline);
          border-radius: 14px;
          padding: 0.95rem;
          display: flex;
          flex-direction: column;
          gap: 0.7rem;
          transition: border-color .2s ease, box-shadow .2s ease;
        }
        .hadacy-image-card:hover {
          border-color: rgba(184, 147, 58, 0.45);
          box-shadow: 0 8px 24px rgba(26, 20, 16, 0.06);
        }
        .hadacy-image-card--deleting { opacity: 0.55; }
        .hadacy-image-card__thumb {
          width: 100%;
          background: var(--recess);
          border-radius: 10px;
          overflow: hidden;
          aspect-ratio: 16/9;
          display: flex;
          align-items: center;
          justify-content: center;
          position: relative;
        }
        .hadacy-image-card__thumb img {
          width: 100%;
          height: 100%;
          object-fit: cover;
          display: block;
        }
        .hadacy-image-card__placeholder {
          color: var(--muted);
          font-size: 0.78rem;
          font-style: italic;
          text-align: center;
          padding: 0.6rem;
          line-height: 1.4;
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 0.3rem;
        }
        .hadacy-image-card__placeholder i {
          font-size: 1.5rem;
          color: var(--gold);
          opacity: 0.6;
        }
        .hadacy-image-card__head {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          gap: 0.6rem;
        }
        .hadacy-image-card__label {
          font-size: 0.94rem;
          color: var(--dark);
          margin: 0;
          font-weight: 500;
          line-height: 1.3;
        }
        .hadacy-image-card__where {
          font-size: 0.78rem;
          color: var(--muted);
          line-height: 1.45;
          margin: 0;
        }
        .hadacy-image-card__rec {
          font-size: 0.74rem;
          color: var(--muted);
          line-height: 1.45;
          margin: 0;
          padding-top: 0.5rem;
          border-top: 1px solid var(--hairline);
        }
        .hadacy-image-card__rec strong {
          color: var(--dark);
          font-weight: 500;
        }
        .hadacy-image-card__foot {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 0.5rem;
          margin-top: auto;
          flex-wrap: wrap;
        }
        .hadacy-image-card__key {
          font-family: "Courier New", monospace;
          font-size: 0.7rem;
          color: var(--muted);
          letter-spacing: 0.04em;
          word-break: break-all;
        }
        .hadacy-image-card__actions {
          display: flex;
          gap: 0.4rem;
        }
        .hadacy-image-card__icon {
          width: 32px; height: 32px;
          border-radius: 999px;
          border: 1px solid var(--hairline);
          background: transparent;
          color: var(--muted);
          font-size: 0.75rem;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          transition: background .2s ease, color .2s ease, border-color .2s ease;
        }
        .hadacy-image-card__icon:hover {
          background: var(--recess);
          color: var(--dark);
          border-color: var(--gold);
        }
        .hadacy-image-card__icon--danger:hover {
          background: rgba(176, 74, 60, 0.1);
          color: var(--danger);
          border-color: var(--danger);
        }
        .hadacy-image-card__icon:disabled { opacity: 0.55; cursor: not-allowed; }
        .hadacy-image-card__upload-link {
          font-family: "Jost", sans-serif;
          font-size: 0.7rem;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          background: transparent;
          border: 1px solid var(--hairline);
          color: var(--gold);
          padding: 0.45rem 0.85rem;
          border-radius: 999px;
          cursor: pointer;
          transition: background .2s ease, color .2s ease, border-color .2s ease;
        }
        .hadacy-image-card__upload-link:hover {
          background: var(--dark);
          color: var(--gold-light);
          border-color: var(--dark);
        }

        /* Status pills */
        .hadacy-images-pill {
          display: inline-flex;
          align-items: center;
          gap: 0.35rem;
          padding: 0.2rem 0.55rem;
          border-radius: 999px;
          font-size: 0.62rem;
          letter-spacing: 0.16em;
          text-transform: uppercase;
          border: 1px solid;
          background: transparent;
          font-weight: 500;
        }
        .hadacy-images-pill::before {
          content: "";
          display: inline-block;
          width: 6px; height: 6px;
          border-radius: 999px;
          background: currentColor;
        }
        .hadacy-images-pill--ok {
          color: var(--success);
          border-color: rgba(91, 122, 60, 0.4);
          background: rgba(91, 122, 60, 0.08);
        }
        .hadacy-images-pill--empty {
          color: var(--muted);
          border-color: var(--hairline);
          background: var(--recess);
        }

        /* Banners */
        .hadacy-images-banner {
          padding: 0.7rem 0.95rem;
          border-radius: 10px;
          margin-bottom: 1rem;
          font-size: 0.86rem;
          line-height: 1.45;
          border: 1px solid;
          display: flex;
          align-items: flex-start;
          gap: 0.55rem;
        }
        .hadacy-images-banner i { margin-top: 2px; }
        .hadacy-images-banner--danger {
          background: rgba(176, 74, 60, 0.08);
          border-color: rgba(176, 74, 60, 0.32);
          color: var(--danger);
        }
        .hadacy-images-banner--info {
          background: rgba(184, 147, 58, 0.10);
          border-color: rgba(184, 147, 58, 0.32);
          color: var(--gold);
        }

        /* Toast */
        .hadacy-images-toast {
          position: fixed;
          right: 1.2rem;
          bottom: 1.2rem;
          z-index: 1080;
          background: var(--card-bg);
          border: 1px solid var(--hairline);
          border-left: 4px solid var(--gold);
          border-radius: 10px;
          padding: 0.7rem 1rem;
          font-size: 0.88rem;
          color: var(--dark);
          box-shadow: 0 14px 40px rgba(26, 20, 16, 0.14);
          max-width: 360px;
          animation: hadacy-images-toast-in .25s ease both;
        }
        .hadacy-images-toast--ok { border-left-color: var(--success); }
        .hadacy-images-toast--warn { border-left-color: var(--gold); }
        .hadacy-images-toast--danger { border-left-color: var(--danger); }
        @keyframes hadacy-images-toast-in {
          from { transform: translateY(8px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
      `}</style>

      <header className="hadacy-images__header">
        <div className="hadacy-images__eyebrow">HADACY · administración</div>
        <h1 className="hadacy-images__title">Imágenes del sitio</h1>
        <p className="hadacy-images__lede">
          Estas son las imágenes que aparecen en tu tienda. Cada espacio de la
          home tiene su propio "slot" — escoge uno para subirle una imagen
          nueva o reemplazar la que ya tiene.
        </p>
      </header>

      <section className="hadacy-images-help">
        <div className="hadacy-images-help__item">
          <span className="hadacy-images-help__num">1</span>
          <div>
            <h3 className="hadacy-images-help__title">Elige un slot</h3>
            <p className="hadacy-images-help__body">
              En el directorio de abajo encontrarás cada espacio del sitio con
              su nombre, ubicación y dimensiones recomendadas.
            </p>
          </div>
        </div>
        <div className="hadacy-images-help__item">
          <span className="hadacy-images-help__num">2</span>
          <div>
            <h3 className="hadacy-images-help__title">Sube tu imagen</h3>
            <p className="hadacy-images-help__body">
              Toca "Subir aquí" en el slot vacío que quieras llenar. Validamos
              tamaño y proporción antes de enviarla a Cloudinary.
            </p>
          </div>
        </div>
        <div className="hadacy-images-help__item">
          <span className="hadacy-images-help__num">3</span>
          <div>
            <h3 className="hadacy-images-help__title">Reemplaza cuando quieras</h3>
            <p className="hadacy-images-help__body">
              Para cambiar una imagen ya subida, primero elimínala desde su
              tarjeta y luego sube la nueva en su lugar.
            </p>
          </div>
        </div>
      </section>

      {/* ── UPLOAD PANEL ──────────────────────────────────────────────── */}
      <div ref={formRef} className="hadacy-images-panel">
        <div className="hadacy-images-panel__head">
          <h2 className="hadacy-images-panel__title">Subir nueva imagen</h2>
          <span className="hadacy-images-panel__hint">
            {usedKeys.size} de {KNOWN_KEYS.length} slots ocupados
          </span>
        </div>

        {allKeysUsed && (
          <div className="hadacy-images-banner hadacy-images-banner--info">
            <i className="fas fa-info-circle"></i>
            <span>
              Todos los slots están ocupados. Para subir una imagen nueva,
              elimina primero la que quieras reemplazar desde su tarjeta.
            </span>
          </div>
        )}

        <form onSubmit={onCreateOrReplace} noValidate>
          <div className="hadacy-images-upload">
            <div>
              <div className="hadacy-images-section">
                <h3 className="hadacy-images-section__title">
                  <span>1 · Slot</span>
                </h3>

                <div className="hadacy-images-field">
                  <label htmlFor="img-key" className="hadacy-images-label">
                    Espacio del sitio
                  </label>
                  <select
                    id="img-key"
                    className="hadacy-images-select"
                    value={keyName}
                    onChange={(e) => setKeyName(e.target.value)}
                    disabled={allKeysUsed}
                  >
                    {SLOT_GROUPS.map((g) => (
                      <optgroup key={g.id} label={g.label}>
                        {SLOTS.filter((s) => s.group === g.id).map((s) => {
                          const inUse = usedKeys.has(s.key);
                          return (
                            <option
                              key={s.key}
                              value={s.key}
                              disabled={inUse}
                            >
                              {s.label}
                              {inUse ? " · ocupado" : ""}
                            </option>
                          );
                        })}
                      </optgroup>
                    ))}
                  </select>
                </div>

                {activeSlot && (
                  <div className="hadacy-images-slot-detail">
                    <div className="hadacy-images-slot-detail__label">
                      Acerca de este slot
                    </div>
                    <h4 className="hadacy-images-slot-detail__title">
                      {activeSlot.label}
                    </h4>
                    <p className="hadacy-images-slot-detail__body">
                      {activeSlot.where}
                      <br />
                      <strong>Recomendado:</strong> {activeSlot.recommended}
                    </p>
                  </div>
                )}
              </div>

              <div className="hadacy-images-section">
                <h3 className="hadacy-images-section__title">
                  <span>2 · Texto alternativo</span>
                </h3>

                <div className="hadacy-images-field">
                  <label htmlFor="img-alt" className="hadacy-images-label">
                    Alt text (descripción)
                  </label>
                  <input
                    id="img-alt"
                    type="text"
                    className="hadacy-images-input"
                    placeholder="Modelo aplicando crema facial"
                    value={alt}
                    onChange={(e) => setAlt(e.target.value)}
                    disabled={allKeysUsed}
                  />
                  <span className="hadacy-images-help-text">
                    Lo leen los lectores de pantalla y aparece si la imagen no
                    carga. Describe lo que se ve, sin escribir "imagen de…".
                  </span>
                </div>
              </div>

              <div className="hadacy-images-section">
                <h3 className="hadacy-images-section__title">
                  <span>3 · Archivo</span>
                </h3>

                <div className="hadacy-images-field">
                  <label
                    className={`hadacy-images-drop ${
                      file ? "hadacy-images-drop--has-file" : ""
                    } ${allKeysUsed ? "hadacy-images-drop--disabled" : ""}`}
                  >
                    <div className="hadacy-images-drop__icon">
                      <i
                        className={
                          file
                            ? "fas fa-check-circle"
                            : "fas fa-cloud-upload-alt"
                        }
                      ></i>
                    </div>
                    <p className="hadacy-images-drop__title">
                      {file
                        ? file.name
                        : "Selecciona o arrastra una imagen aquí"}
                    </p>
                    <p className="hadacy-images-drop__body">
                      {file
                        ? `Tamaño original: ${fmtMB(file.size)} MB · ${
                            file.size >= MAX_BYTES
                              ? "se comprimirá automáticamente"
                              : "se optimizará al subir"
                          }`
                        : "JPG, PNG o WEBP · hasta 10 MB · convertimos a WebP"}
                    </p>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onClick={(e) => {
                        e.currentTarget.value = "";
                      }}
                      onChange={(e) => {
                        const picked = e.target.files?.[0] || null;
                        setFile(picked);
                        if (picked && picked.size >= MAX_BYTES) {
                          showToast(
                            `El archivo pesa ${fmtMB(picked.size)} MB. Se intentará comprimir a menos de ${MAX_MB} MB.`,
                            "warn"
                          );
                        }
                      }}
                      disabled={allKeysUsed}
                    />
                  </label>
                </div>
              </div>

              <div className="hadacy-images-actions">
                <button
                  type="submit"
                  className="hadacy-images-cta"
                  disabled={uploading || allKeysUsed}
                >
                  {uploading ? (
                    "Subiendo…"
                  ) : (
                    <>
                      Guardar imagen <i className="fas fa-arrow-right"></i>
                    </>
                  )}
                </button>
                {file && (
                  <button
                    type="button"
                    className="hadacy-images-cta hadacy-images-cta--ghost"
                    disabled={uploading}
                    onClick={() => {
                      setFile(null);
                      if (fileInputRef.current)
                        fileInputRef.current.value = "";
                    }}
                  >
                    Quitar archivo
                  </button>
                )}
              </div>
            </div>

            <div className="hadacy-images-preview">
              <div className="hadacy-images-preview__label">
                Vista previa local
              </div>
              <div className="hadacy-images-preview__frame">
                {file ? (
                  <img alt="preview" src={URL.createObjectURL(file)} />
                ) : (
                  <span className="hadacy-images-preview__empty">
                    Selecciona un archivo para previsualizar.
                  </span>
                )}
              </div>
            </div>
          </div>
        </form>
      </div>

      {/* ── DIRECTORY ─────────────────────────────────────────────────── */}
      <div className="hadacy-images-search">
        <i className="fas fa-search hadacy-images-search__icon"></i>
        <input
          type="text"
          className="hadacy-images-input hadacy-images-search__input"
          placeholder="Buscar por nombre del slot, alt o publicId…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
      </div>

      {error && (
        <div className="hadacy-images-banner hadacy-images-banner--danger">
          <i className="fas fa-exclamation-circle"></i>
          <span>{error}</span>
        </div>
      )}

      {loading ? (
        <Loader />
      ) : (
        SLOT_GROUPS.map((g) => {
          const groupSlots = slotsByGroup[g.id] || [];
          if (groupSlots.length === 0) return null;
          const filledInGroup = groupSlots.filter((s) =>
            usedKeys.has(s.key)
          ).length;
          return (
            <section className="hadacy-images-group" key={g.id}>
              <div className="hadacy-images-group__head">
                <h2 className="hadacy-images-group__title">{g.label}</h2>
                <span className="hadacy-images-group__count">
                  {filledInGroup}/{groupSlots.length} ocupados
                </span>
              </div>
              <div className="hadacy-images-grid">
                {groupSlots.map((slot) => {
                  const img = imagesByKey[slot.key];
                  const isFilled = !!img;
                  const isDeleting = img && deletingIds.has(img._id);
                  return (
                    <article
                      key={slot.key}
                      className={`hadacy-image-card ${
                        isDeleting ? "hadacy-image-card--deleting" : ""
                      }`}
                    >
                      <div className="hadacy-image-card__thumb">
                        {isFilled ? (
                          <img
                            src={img.secureUrl || img.url}
                            alt={img.alt || slot.label}
                          />
                        ) : (
                          <div className="hadacy-image-card__placeholder">
                            <i className="far fa-image"></i>
                            <span>Slot vacío</span>
                          </div>
                        )}
                      </div>
                      <div className="hadacy-image-card__head">
                        <h3 className="hadacy-image-card__label">
                          {slot.label}
                        </h3>
                        <span
                          className={`hadacy-images-pill ${
                            isFilled
                              ? "hadacy-images-pill--ok"
                              : "hadacy-images-pill--empty"
                          }`}
                        >
                          {isFilled ? "Configurado" : "Vacío"}
                        </span>
                      </div>
                      <p className="hadacy-image-card__where">{slot.where}</p>
                      <p className="hadacy-image-card__rec">
                        <strong>Recomendado:</strong> {slot.recommended}
                        {isFilled && (
                          <>
                            <br />
                            <strong>Actualizado:</strong>{" "}
                            {fmtDate(img.updatedAt)}
                          </>
                        )}
                      </p>
                      <div className="hadacy-image-card__foot">
                        <span className="hadacy-image-card__key">
                          {slot.key}
                        </span>
                        <div className="hadacy-image-card__actions">
                          {isFilled ? (
                            <>
                              <a
                                className="hadacy-image-card__icon"
                                href={img.secureUrl || img.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label="Ver imagen completa"
                                title="Ver imagen completa"
                              >
                                <i className="fas fa-external-link-alt"></i>
                              </a>
                              <button
                                type="button"
                                className="hadacy-image-card__icon hadacy-image-card__icon--danger"
                                onClick={() => onDelete(img)}
                                disabled={isDeleting}
                                aria-label="Eliminar"
                                title="Eliminar"
                              >
                                <i
                                  className={
                                    isDeleting
                                      ? "fas fa-spinner fa-spin"
                                      : "fas fa-trash"
                                  }
                                ></i>
                              </button>
                            </>
                          ) : (
                            <button
                              type="button"
                              className="hadacy-image-card__upload-link"
                              onClick={() => onPickSlot(slot)}
                            >
                              Subir aquí
                            </button>
                          )}
                        </div>
                      </div>
                    </article>
                  );
                })}
              </div>
            </section>
          );
        })
      )}

      {!loading && filteredSlots.length === 0 && (
        <div className="hadacy-images-banner hadacy-images-banner--info">
          <i className="fas fa-search"></i>
          <span>
            No hay slots que coincidan con "{filter}". Borra el buscador para
            ver el catálogo completo.
          </span>
        </div>
      )}

      {toast.show && (
        <div
          className={`hadacy-images-toast hadacy-images-toast--${toast.tone}`}
          role="status"
        >
          {toast.message}
        </div>
      )}
    </div>
  );
};

export default SiteImagesAdminScreen;
